import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings, settingsImageUrl } from "../context/SettingsContext";
import { NAV_ITEMS, canAccess, ROLE_LABELS, ROLE_COLORS } from "../lib/constants";
import { LogOut, MessageSquare, Leaf } from "lucide-react";
import ChatDrawer from "./ChatDrawer";
import NotificationsBell from "./Notifications";
import TextCustomizer from "./TextCustomizer";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [chatOpen, setChatOpen] = useState(false);

  const items = NAV_ITEMS.filter((i) => canAccess(i, user));
  const appName = settings?.app_name || "OrtoFrutta";
  const slogan = settings?.slogan || "Manager";
  const logoUrl = settingsImageUrl("logo_file_id", settings);

  const NavButton = ({ item, mobile }) => {
    const active = location.pathname === item.path;
    const Icon = item.icon;
    return (
      <button
        data-testid={`nav-${item.path === "/" ? "dashboard" : item.path.slice(1)}`}
        onClick={() => navigate(item.path)}
        className={`flex ${mobile ? "flex-col gap-1 flex-1 py-2" : "flex-row gap-3 px-4 py-3 w-full"} items-center rounded-md text-sm font-medium transition-colors duration-200 ${
          active
            ? "bg-primary/15 text-primary"
            : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
        }`}
      >
        <Icon className={mobile ? "w-5 h-5" : "w-5 h-5 shrink-0"} strokeWidth={2} />
        <span className={mobile ? "text-[10px] uppercase tracking-wider" : ""}>{item.label}</span>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-slate-800 bg-slate-900/40 sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-slate-800">
          <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center overflow-hidden">
            {logoUrl ? <img src={logoUrl} alt="logo" className="w-full h-full object-cover" /> : <Leaf className="w-5 h-5 text-slate-950" />}
          </div>
          <div className="leading-tight">
            <div className="font-display font-black tracking-tight text-lg text-slate-50">{appName}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{slogan}</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((item) => (
            <NavButton key={item.path} item={item} />
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800 space-y-2">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center font-bold text-slate-200 uppercase">
              {user.name?.[0] || "U"}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-100 truncate">{user.name}</div>
              <span className={`inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${ROLE_COLORS[user.role]}`}>
                {ROLE_LABELS[user.role]}
              </span>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-4 md:px-8 border-b border-slate-800 backdrop-blur-xl bg-slate-950/80">
          <div className="flex items-center gap-2 md:hidden">
            <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center overflow-hidden">
              {logoUrl ? <img src={logoUrl} alt="logo" className="w-full h-full object-cover" /> : <Leaf className="w-4 h-4 text-slate-950" />}
            </div>
            <span className="font-display font-black text-slate-50">{appName}</span>
          </div>
          <div className="hidden md:block font-display font-bold text-slate-300 uppercase tracking-wide text-sm">
            {ROLE_LABELS[user.role]}
          </div>
          <div className="flex items-center gap-2">
            <NotificationsBell />
            <button
              data-testid="open-chat-btn"
              onClick={() => setChatOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium transition-colors"
            >
              <MessageSquare className="w-4 h-4" /> Chat team
            </button>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8 max-w-[1400px] w-full">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 flex items-stretch border-t border-slate-800 bg-slate-950/95 backdrop-blur-xl px-1">
        {items.slice(0, 5).map((item) => (
          <NavButton key={item.path} item={item} mobile />
        ))}
      </nav>

      <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
      <TextCustomizer />
    </div>
  );
}
